import type { SigninRedirectArgs } from 'oidc-client-ts';
import type { Observable } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, NgZone, PLATFORM_ID, signal, untracked } from '@angular/core';
import dayjs from 'dayjs/esm';
import { catchError, EMPTY, first, from, map, of, switchMap } from 'rxjs';

import { isNonEmptyString, OidcService } from '@accesso/cc-std-lib';
import { ProfileModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';

import type { ComplexPrimitiveObject } from '../../feature/storage/storage.model';
import type { IAuthService } from './auth.models';
import { MerchantStore } from '../../feature/merchant/merchant.store';
import { StorageKey } from '../../feature/storage/storage.keys';
import { StorageService } from '../../feature/storage/storage.token';
import { isTrackingQueryParam } from '../utils/tracking-query-params';

interface AnonymousTokenResponse {
	access_token: string;
	exp: number;
	refresh_token: string;
}
export interface AnonTokenLocalStorageValue extends ComplexPrimitiveObject {
	a: string; // access token
	b: number; // expiration date (unix epoch milliseconds)
	c: string; // refresh token
}

@Injectable({
	providedIn: 'root',
})
export class AuthService implements IAuthService {
	private readonly zone = inject(NgZone);
	private readonly merchantStore = inject(MerchantStore);
	private readonly jsonApi = inject(JsonApiService);
	private readonly oidc = inject(OidcService);
	private readonly http = inject(HttpClient);
	private readonly storageService = inject(StorageService);
	private _profile = signal<ProfileModel | null>(null);
	private authority = '';
	private anonClientId = '';
	private refreshTimer: ReturnType<typeof setTimeout> | null = null;
	private isServer = isPlatformServer(inject(PLATFORM_ID));

	user = this.oidc.user;
	profile = this._profile.asReadonly();
	authLoading = this.oidc.isLoading;
	isLoaded = this.oidc.isLoaded;

	constructor() {
		if (this.isServer) return;

		const destroyRef = effect(() => {
			const store = this.merchantStore.store();
			if (store?.state().loaded) {
				this.authority = `https://${store.authDomain()}`;
				this.anonClientId = store.anonClientId();
				destroyRef.destroy();

				const anonToken = this.getAnonymousToken();
				if (anonToken) {
					this.fetchAnonymousToken(this.authority, this.anonClientId, {
						grantType: 'refresh_token',
						refreshToken: anonToken,
					})
						.pipe(
							catchError(() => {
								this.storageService.remove(StorageKey.ANON_TOKEN);
								return EMPTY;
							})
						)
						.subscribe();
				}
			}
		});

		effect(() => {
			const user = this.oidc.user() as unknown as { profile: Record<'auid', string> } | null;
			untracked(() => this._profile.set(user ? this.jsonApi.findOne(ProfileModel, user.profile.auid) : null));
		});
	}

	initialize() {
		const store = this.merchantStore.store();
		if (!store || !this.merchantStore.merchant()?.usesProfiles()) {
			return Promise.resolve();
		}
		return this.oidc.initialize();
	}

	isAuthenticated() {
		return this.oidc.isAuthenticated();
	}

	getUser() {
		return this.oidc.getUser();
	}

	signIn(options: SigninRedirectArgs) {
		return this.oidc.signIn(options);
	}

	/**
	 * Filters a set of query parameters down to only the UTM and Google tracking parameters that should be
	 * carried through the accesso ID sign-in round trip and preserved across in-app navigation.
	 * @param params The query parameters to filter, e.g. from `ActivatedRouteSnapshot.queryParams`
	 * @returns A new object containing only the recognized tracking query parameters with non-null, non-undefined string values
	 */
	getTrackingQueryParams(params: Record<string, string | undefined | null>): Record<string, string> {
		return Object.entries(params).reduce<Record<string, string>>((trackingParams, [key, value]) => {
			if (isTrackingQueryParam(key) && isNonEmptyString(value)) {
				trackingParams[key] = value;
			}
			return trackingParams;
		}, {});
	}

	signInCallback() {
		return this.oidc.signInCallback();
	}

	signOut() {
		return this.oidc.signOut();
	}

	signOutCallback() {
		return this.oidc.signOutCallback();
	}

	getAccessToken(): Observable<string | null> {
		const merchant = this.merchantStore.merchant();
		if (!merchant?.usesProfiles()) {
			const anonToken = this.storageService.get<AnonTokenLocalStorageValue>(StorageKey.ANON_TOKEN)?.a;
			if (isNonEmptyString(anonToken)) {
				return of(anonToken);
			}
			return this.fetchAnonymousToken(this.authority, this.anonClientId);
		}

		return from(this.getUser()).pipe(
			switchMap((user) => {
				const authToken = user?.access_token;
				const anonToken = this.storageService.get<AnonTokenLocalStorageValue>(StorageKey.ANON_TOKEN)?.a;

				if (isNonEmptyString(authToken)) {
					return of(authToken);
				} else if (isNonEmptyString(anonToken)) {
					return of(anonToken);
				}

				return this.fetchAnonymousToken(this.authority, this.anonClientId);
			})
		);
	}

	getAnonymousToken(): string | null {
		const storedValue = this.storageService.get<AnonTokenLocalStorageValue>(StorageKey.ANON_TOKEN);
		return storedValue?.a ?? null;
	}

	clearStaleState() {
		return this.oidc.clearStaleState();
	}

	fetchAnonymousToken(authUrl: string, anonClientId: string, options?: { grantType?: string; refreshToken?: string }): Observable<string | null> {
		return this.http
			.get<AnonymousTokenResponse>(`${authUrl}/internal/anonymousToken`, {
				headers: {
					client_id: anonClientId,
					...(options
						? {
								grant_type: options.grantType,
								refresh_token: options.refreshToken,
							}
						: {}),
				},
			})
			.pipe(
				first(),
				map(({ access_token, exp, refresh_token }) => {
					const localStorageValue: AnonTokenLocalStorageValue = { a: access_token, b: exp, c: refresh_token };
					this.storageService.add(StorageKey.ANON_TOKEN, localStorageValue);

					if (!this.refreshTimer) {
						this.zone.runOutsideAngular(() => {
							this.refreshTimer = setTimeout(
								() => {
									this.refreshTimer = null;
									this.fetchAnonymousToken(authUrl, anonClientId, {
										grantType: 'refresh_token',
										refreshToken: refresh_token,
									})
										.pipe(
											catchError(() => {
												this.refreshTimer = null;
												this.storageService.remove(StorageKey.ANON_TOKEN);
												return EMPTY;
											})
										)
										.subscribe();
								},
								// Min 1 minutes
								Math.max(60000, (exp - dayjs().unix()) * 1000)
							);
						});
					}

					return access_token;
				})
			);
	}
}
